import { Box, CloseButton, Divider, PopoverMenu, Text, TextButton, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/i18n';
import { useExperiments } from '@wix/fe-essentials/react';
import { CodeSmall, HelpCircleSmall, More, RefreshSmall } from '@wix/wix-ui-icons-common';
import React, { useEffect, useMemo, useState } from 'react';
import { BiCtaNames } from '../../bi/types';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { TabsWrapper } from '../../panel/a11y-wizard-panel-tabs';
import { EditorHost } from '../../providers';
import { dataHooks } from '../../utils/datahooks';
import { isClassicEditorHost, isEditor3Host, isStudioHost } from '../../utils/get-editor-host';
import { useExposeAdvancedSettingsModal } from '../modals/use-expose-advanced-settings-modal';
export const WizardHeader = _ref => {
  let {
    isEmptyState,
    onClickClose,
    onRescanClick,
    detectedIssuesNumber,
    manualTasksNumber
  } = _ref;
  const [hasAdvancedSettings, setHasAdvancedSettings] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    editorHost,
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const biLogger = useBiLogger();
  const {
    showAdvancedSettingsModal
  } = useExposeAdvancedSettingsModal();
  const {
    experiments
  } = useExperiments();
  const useNewCardsUI = experiments.enabled('specs.a11y-wizard.useCardsUI');
  const menuPlacement = isStudioHost(editorHost) ? 'bottom-start' : 'left-start';
  const isEditor3 = isEditor3Host(editorHost);
  useEffect(() => {
    if (isClassicEditorHost(editorHost)) {
      const fetchAdvancedSettings = async () => {
        const value = await a11yWizardAdapter.getHasAdvancedSettings();
        if (value !== hasAdvancedSettings) {
          setHasAdvancedSettings(value);
        }
      };
      fetchAdvancedSettings();
    }
  }, [a11yWizardAdapter, editorHost, hasAdvancedSettings]);
  const menuItems = useMemo(() => {
    const items = [{
      dataHook: dataHooks.a11yWizardHeaderPopoverMenuOptionLearnMore,
      icon: /*#__PURE__*/React.createElement(HelpCircleSmall, {
        "aria-hidden": true
      }),
      key: 'accessibility-wizard.header.more-action-button.popover.learn-more',
      onClick: () => {
        biLogger.ctaClick({
          ctaName: BiCtaNames.MenuLearnMoreAboutTheWizard
        });
        openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-about-the-wizard-help');
      }
    }];
    if (!isEmptyState) {
      items.push({
        dataHook: dataHooks.a11yWizardHeaderPopoverMenuOptionScanAgain,
        icon: /*#__PURE__*/React.createElement(RefreshSmall, {
          "aria-hidden": true
        }),
        key: 'accessibility-wizard.header.more-action-button.popover.rescan',
        onClick: onRescanClick
      });
    }
    if (isClassicEditorHost(editorHost)) {
      items.push({
        dataHook: dataHooks.a11yWizardHeaderPopoverMenuOptionExposeDevTools,
        icon: /*#__PURE__*/React.createElement(CodeSmall, {
          "aria-hidden": true
        }),
        key: hasAdvancedSettings ? 'accessibility-wizard.header.more-action-button.popover.dev-tools.hide' : 'accessibility-wizard.header.more-action-button.popover.dev-tools.show',
        onClick: () => {
          biLogger.ctaClick({
            ctaName: hasAdvancedSettings ? BiCtaNames.MenuHideAdvancedSettings : BiCtaNames.MenuShowAdvancedSettings
          });
          showAdvancedSettingsModal(hasAdvancedSettings, setHasAdvancedSettings);
        }
      });
    }
    return items.map(_ref2 => {
      let {
        key,
        icon,
        onClick,
        dataHook
      } = _ref2;
      return /*#__PURE__*/React.createElement(PopoverMenu.MenuItem, {
        dataHook: dataHook,
        key: key,
        prefixIcon: icon,
        text: t(key),
        onClick: onClick
      });
    });
  }, [biLogger, editorHost, hasAdvancedSettings, isEmptyState, onRescanClick, openSelectedArticle, showAdvancedSettingsModal, t]);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      background: '#FFF',
      position: 'sticky',
      top: 0,
      zIndex: 10
    }
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '16px 16px 16px 24px'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "bold"
  }, t('accessibility-wizard.header.title')), isEditor3 && /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin"
  }, t('accessibility-wizard.header.subtitle'))), /*#__PURE__*/React.createElement(Box, {
    align: "right"
  }, /*#__PURE__*/React.createElement(PopoverMenu, {
    dataHook: dataHooks.a11yWizardHeaderPopoverMenu,
    placement: menuPlacement,
    textSize: "small",
    triggerElement: /*#__PURE__*/React.createElement(Tooltip, {
      content: t('accessibility-wizard.header.more-action-button.tooltip'),
      dataHook: dataHooks.a11yWizardHeaderMoreActionTooltip,
      placement: "top",
      size: "small"
    }, /*#__PURE__*/React.createElement(Box, {
      marginTop: "4px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(TextButton, {
      "aria-label": t('accessibility-wizard.header.more-action-button.more-actions.aria-label'),
      dataHook: dataHooks.a11yWizardHeaderMoreActionButton,
      skin: "dark",
      onClick: () => biLogger.ctaClick({
        ctaName: BiCtaNames.MoreActions
      })
    }, /*#__PURE__*/React.createElement(More, null)))),
    zIndex: 5000
  }, menuItems), /*#__PURE__*/React.createElement(CloseButton, {
    "aria-label": t('accessibility-wizard.header.more-action-button.close.aria-label'),
    dataHook: dataHooks.a11yWizardHeaderClosePanelButton,
    size: "large",
    skin: "dark",
    onClick: onClickClose
  }))), editorHost !== EditorHost.odeditor && !useNewCardsUI && /*#__PURE__*/React.createElement(Divider, null)), !isEmptyState && editorHost !== EditorHost.odeditor && !useNewCardsUI && /*#__PURE__*/React.createElement(TabsWrapper, {
    detectedIssuesNumber: detectedIssuesNumber,
    manualTasksNumber: manualTasksNumber
  }));
};
//# sourceMappingURL=wizard-header.js.map